import React, { useState } from 'react';
import { Upload, Download, Plus, Trash2, MapPin, Pencil } from 'lucide-react';
import * as XLSX from 'xlsx';
import { TENSIONS, FORMULES_PAR_TENSION, CONTRATS } from './constants';

export const StepPDLConfig = ({ analyseType, formData, setFormData, onPrev, onNext, onSaveDraft }) => {
  const [currentPDL, setCurrentPDL] = useState({
    pdl: '', tension: '', formule_actuelle: '', contrat: '', puissances_actuelles: {}, commentaire: ''
  });
  const [showImportPDL, setShowImportPDL] = useState(false);
  const [architecturePDL, setArchitecturePDL] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const downloadTemplatePDL = () => {
    const wb = XLSX.utils.book_new();
    const data = [
      ['PDL', 'Tension', 'Formule Tarifaire', 'Type Contrat'],
      ['30001234', 'HTA', 'HTACU5 PF', 'CARD'],
      ['30005678', 'BTSUP', 'BTSUPCU4', 'Unique'],
      ['30009999', 'BTINF', 'BTINFCU4', 'CARD']
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 15 }, { wch: 10 }, { wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws, 'PDL TURPE');
    XLSX.writeFile(wb, 'template_pdl_turpe.xlsx');
    alert('Template téléchargé\n\nRemplissez les PDL avec leur configuration');
  };

  const handleImportPDLExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const pdlsImportes = [];
        for (let rowNum = range.s.r + 1; rowNum <= range.e.r; rowNum++) {
          const row = [];
          for (let colNum = 0; colNum <= 3; colNum++) {
            const cell = worksheet[XLSX.utils.encode_cell({r: rowNum, c: colNum})];
            row.push(cell ? cell.v : null);
          }
          const pdl = row[0] ? String(row[0]) : null;
          const tension = row[1] ? String(row[1]) : null;
          const formule = row[2] ? String(row[2]) : null;
          const contrat = row[3] ? String(row[3]) : null;
          if (pdl && tension && formule && contrat) {
            pdlsImportes.push({ pdl, tension, formule_actuelle: formule, contrat, puissances_actuelles: {} });
          }
        }
        setArchitecturePDL({ pdls: pdlsImportes, nb_pdl: pdlsImportes.length });
        setShowImportPDL(true);
      } catch (error) {
        console.error('Erreur import:', error);
        alert('Erreur lecture fichier');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const validerArchitecturePDL = () => {
    setFormData({ ...formData, pdls: architecturePDL.pdls });
    setShowImportPDL(false);
    setArchitecturePDL(null);
    alert(`${architecturePDL.nb_pdl} PDL importés et validés`);
  };

  const ajouterPDL = () => {
    if (!currentPDL.pdl || !currentPDL.tension || !currentPDL.formule_actuelle || !currentPDL.contrat) {
      alert('Remplissez tous les champs');
      return;
    }
    if (editingIndex !== null) {
      // Mode modification : remplacer le PDL à l'index
      const newPdls = [...formData.pdls];
      newPdls[editingIndex] = { ...currentPDL };
      setFormData({ ...formData, pdls: newPdls });
      setEditingIndex(null);
      setCurrentPDL({ pdl: '', tension: '', formule_actuelle: '', contrat: '', puissances_actuelles: {} });
      alert('PDL modifié');
    } else {
      setFormData({ ...formData, pdls: [...formData.pdls, { ...currentPDL }] });
      setCurrentPDL({ pdl: '', tension: '', formule_actuelle: '', contrat: '', puissances_actuelles: {} });
      alert('PDL ajouté');
    }
  };

  const modifierPDL = (idx) => {
    const pdl = formData.pdls[idx];
    setCurrentPDL({ ...pdl });
    setEditingIndex(idx);
  };

  const annulerModification = () => {
    setEditingIndex(null);
    setCurrentPDL({ pdl: '', tension: '', formule_actuelle: '', contrat: '', puissances_actuelles: {} });
  };

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
        Configuration {analyseType === 'mono' ? 'du PDL' : 'des PDL'}
      </h2>

      {/* Import Excel (multi-PDL uniquement) */}
      {analyseType === 'multi' && formData.pdls.length === 0 && !showImportPDL && (
        <div className="mb-6 grid grid-cols-2 gap-6">
          <div className="p-6 bg-white border-2 border-[#BFC6CC] rounded-lg hover:border-[#397DFF] transition-all text-center">
            <Upload className="w-12 h-12 text-[#397DFF] mx-auto mb-4" />
            <h3 className="font-semibold text-[#191D1E] mb-2">Import Excel</h3>
            <p className="text-sm text-[#687175] mb-4">Importer plusieurs PDL en masse</p>
            <button onClick={downloadTemplatePDL} className="text-sm text-[#397DFF] hover:text-[#4672B6] mb-3">
              <Download className="w-4 h-4 inline mr-1" />Télécharger template
            </button>
            <label className="block">
              <span className="px-4 py-2 bg-[#397DFF] hover:bg-[#4672B6] text-white rounded-lg cursor-pointer inline-block text-sm">
                Charger fichier Excel
              </span>
              <input type="file" accept=".xlsx,.xls" onChange={handleImportPDLExcel} className="hidden" />
            </label>
          </div>
          <div className="p-6 bg-white border-2 border-[#BFC6CC] rounded-lg hover:border-[#397DFF] transition-all text-center">
            <Plus className="w-12 h-12 text-[#687175] mx-auto mb-4" />
            <h3 className="font-semibold text-[#191D1E] mb-2">Ajout Manuel</h3>
            <p className="text-sm text-[#687175] mb-6">Ajouter PDL un par un</p>
          </div>
        </div>
      )}

      {/* Vue synthétique après import */}
      {showImportPDL && architecturePDL && (
        <div className="mb-6 bg-white border-2 border-[#397DFF] rounded-lg p-6">
          <h3 className="font-heading font-semibold text-[#191D1E] mb-4">Architecture Importée - Validation</h3>
          <div className="mb-4 p-4 bg-[#E8F4FD] rounded-lg text-center">
            <div className="text-3xl font-bold text-[#397DFF]">{architecturePDL.nb_pdl}</div>
            <div className="text-sm text-[#687175]">PDL importés</div>
          </div>
          <div className="max-h-60 overflow-y-auto border border-[#BFC6CC] rounded-lg p-4 space-y-2">
            {architecturePDL.pdls.map((pdl, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm p-2 bg-[#F5F6F9] rounded">
                <MapPin className="w-4 h-4 text-[#397DFF]" />
                <span className="font-mono font-semibold">{pdl.pdl}</span>
                <span className="text-[#687175]">{pdl.tension}</span>
                <span className="font-mono text-[#687175]">{pdl.formule_actuelle}</span>
                <span className="text-[#687175]">{pdl.contrat}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => { setShowImportPDL(false); setArchitecturePDL(null); }} className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">Annuler</button>
            <button onClick={validerArchitecturePDL} className="flex-1 bg-[#397DFF] hover:bg-[#4672B6] text-white py-3 rounded-lg">Valider Architecture</button>
          </div>
        </div>
      )}

      {/* Liste PDL ajoutés */}
      {formData.pdls.length > 0 && (
        <div className="mb-6 space-y-3">
          <h3 className="text-sm font-medium text-[#687175]">PDL configurés :</h3>
          {formData.pdls.map((pdl, idx) => (
            <div key={idx} className={`flex items-center gap-4 p-4 border rounded-lg ${editingIndex === idx ? 'bg-[#E8F4FD] border-[#397DFF]' : 'bg-[#F5F6F9] border-[#BFC6CC]'}`}>
              <div className="flex-1 grid grid-cols-4 gap-3 text-sm">
                <div><span className="text-[#687175]">PDL:</span><div className="font-mono font-semibold text-[#191D1E]">{pdl.pdl}</div></div>
                <div><span className="text-[#687175]">Tension:</span><div className="text-[#191D1E]">{pdl.tension}</div></div>
                <div><span className="text-[#687175]">Formule:</span><div className="font-mono text-[#191D1E]">{pdl.formule_actuelle}</div></div>
                <div><span className="text-[#687175]">Contrat:</span><div className="text-[#191D1E]">{pdl.contrat}</div></div>
              </div>
              <button onClick={() => modifierPDL(idx)} className="text-[#397DFF] hover:text-[#4672B6]" data-testid={`turpe-edit-pdl-${idx}`} title="Modifier ce PDL">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => setFormData({...formData, pdls: formData.pdls.filter((_, i) => i !== idx)})} className="text-red-600 hover:text-red-700" data-testid={`turpe-delete-pdl-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire ajout PDL */}
      <div className="border-2 border-dashed border-[#397DFF] rounded-lg p-6 bg-[#E8F4FD]/30">
        <h3 className="font-semibold text-[#191D1E] mb-4">
          {editingIndex !== null ? `Modification du PDL ${currentPDL.pdl}` : formData.pdls.length === 0 ? 'Ajouter le PDL' : 'Ajouter un PDL supplémentaire'}
        </h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-[#687175] mb-1">N° PDL</label>
            <input type="text" value={currentPDL.pdl} onChange={(e) => setCurrentPDL({...currentPDL, pdl: e.target.value})}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg font-mono text-sm" placeholder="30001234..." data-testid="turpe-pdl-number" />
          </div>
          <div>
            <label className="block text-sm text-[#687175] mb-1">Domaine Tension</label>
            <select value={currentPDL.tension} onChange={(e) => setCurrentPDL({...currentPDL, tension: e.target.value, formule_actuelle: ''})}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="turpe-tension">
              <option value="">Sélectionner</option>
              {TENSIONS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#687175] mb-1">Formule Tarifaire Actuelle</label>
            <select value={currentPDL.formule_actuelle} onChange={(e) => setCurrentPDL({...currentPDL, formule_actuelle: e.target.value})}
              disabled={!currentPDL.tension} className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm disabled:bg-[#F5F6F9]" data-testid="turpe-formule">
              <option value="">Sélectionner</option>
              {currentPDL.tension && FORMULES_PAR_TENSION[currentPDL.tension]?.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-[#687175] mb-1">Type de Contrat</label>
            <select value={currentPDL.contrat} onChange={(e) => setCurrentPDL({...currentPDL, contrat: e.target.value})}
              className="w-full px-3 py-2 border border-[#BFC6CC] rounded-lg text-sm" data-testid="turpe-contrat">
              <option value="">Sélectionner</option>
              {CONTRATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={ajouterPDL} className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-6 py-3 rounded-lg" data-testid="turpe-add-pdl">
            {editingIndex !== null ? <><Pencil className="w-5 h-5" /> Valider la modification</> : <><Plus className="w-5 h-5" /> Ajouter ce PDL</>}
          </button>
          {editingIndex !== null && (
            <button onClick={annulerModification} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg hover:bg-[#F5F6F9]">
              Annuler
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-between gap-3 pt-6">
        <button onClick={onPrev} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">← Retour</button>
        <div className="flex gap-3">
          <button onClick={onSaveDraft} className="flex items-center gap-2 px-4 py-2 border border-[#F9C80E] text-[#F9C80E] hover:bg-[#F9C80E] hover:text-white rounded-lg transition-colors">
            Enregistrer Brouillon
          </button>
          <button onClick={onNext} disabled={formData.pdls.length === 0} className="bg-[#397DFF] text-white py-3 px-8 rounded-lg disabled:opacity-50">
            Suivant : Données →
          </button>
        </div>
      </div>
    </div>
  );
};
